import { useState, useRef } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function SmoobuTypeVocabularyPanel() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [progress, setProgress] = useState(null);
  const cancelRef = useRef(false);

  const runAudit = async () => {
    setLoading(true);
    setRows(null);
    setProgress(null);
    cancelRef.current = false;

    // Accumulate rows across chunks (merge by raw_type)
    const merged = {};
    let cursor = null;
    let totalFetched = 0;
    let totalApiErrors = 0;

    try {
      while (true) {
        if (cancelRef.current) break;

        const { data } = await diagnosticsAPI.smoobuTypeVocabulary({
          cursor,
          batchSize: 6,
        });

        totalFetched += data.total_fetched;
        totalApiErrors += data.api_errors;

        for (const row of data.rows) {
          if (merged[row.raw_type]) {
            merged[row.raw_type].count += row.count;
            // Keep at most 2 samples total
            const existing = merged[row.raw_type].samples;
            for (const s of row.samples) {
              if (existing.length < 2) existing.push(s);
            }
          } else {
            merged[row.raw_type] = { ...row };
          }
        }

        setProgress({
          chunkEnd: data.chunk_end,
          windowEnd: data.window_end,
          totalFetched,
          apiErrors: totalApiErrors,
        });

        if (data.done || !data.next_cursor) break;
        cursor = data.next_cursor;
      }

      // Convert merged map to sorted array
      const final = Object.values(merged).sort((a, b) => b.count - a.count);
      setRows(final);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    cancelRef.current = true;
  };

  const hasFlagged = rows?.some((r) => r.flagged);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Smoobu Type Vocabulary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Samples the raw Smoobu API (last 12 months + next 12 months) to discover
          every distinct value of the <code>type</code> field. Confirms whether
          our <code>== &quot;cancellation&quot;</code> match catches every cancel-like variant.
        </p>

        <div className="flex gap-2">
          <Button onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Audit
          </Button>
          {loading && (
            <Button variant="outline" onClick={cancel}>
              Cancel
            </Button>
          )}
        </div>

        {/* Progress while running */}
        {loading && progress && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              Scanned through {progress.chunkEnd} of {progress.windowEnd}
              {' '}({progress.totalFetched} reservations fetched
              {progress.apiErrors > 0 && `, ${progress.apiErrors} API errors`})
            </p>
          </div>
        )}

        {/* Results */}
        {rows && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Distinct types</p>
                <p className="text-xl font-bold tabular-nums">{rows.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total reservations</p>
                <p className="text-xl font-bold tabular-nums">
                  {rows.reduce((s, r) => s + r.count, 0)}
                </p>
              </div>
              <div className={`rounded-lg border p-3 ${hasFlagged ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''}`}>
                <p className="text-xs text-muted-foreground">Uncaught cancel-like</p>
                <p className={`text-xl font-bold tabular-nums ${hasFlagged ? 'text-red-600' : ''}`}>
                  {rows.filter((r) => r.flagged).length}
                </p>
              </div>
            </div>

            {/* Verdict */}
            {hasFlagged ? (
              <div className="flex items-start gap-2 rounded-lg border border-red-500 bg-red-50 dark:bg-red-950/20 p-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-red-700 dark:text-red-400">
                    Gap detected: cancel-like type values not caught by current mapping.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    The flagged rows below contain cancel/void/decline-like strings
                    but map to &quot;confirmed&quot; under our current <code>== &quot;cancellation&quot;</code> check.
                    These need investigation.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-green-500 bg-green-50 dark:bg-green-950/20 p-3">
                <div className="text-sm">
                  <p className="font-medium text-green-700 dark:text-green-400">
                    No gaps found. Every cancel-like type value is caught.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    The <code>== &quot;cancellation&quot;</code> match covers all cancel-like
                    type values observed in the API.
                  </p>
                </div>
              </div>
            )}

            {/* Main table */}
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium">Type value</th>
                    <th className="text-right px-3 py-2 font-medium">Count</th>
                    <th className="text-left px-3 py-2 font-medium">Maps to</th>
                    <th className="text-left px-3 py-2 font-medium">Sample IDs</th>
                    <th className="text-left px-3 py-2 font-medium">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.raw_type}
                      className={`border-b last:border-0 ${r.flagged ? 'bg-red-50 dark:bg-red-950/20' : ''}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{r.raw_type}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                      <td className="px-3 py-2">
                        <Badge variant={r.maps_to === 'cancelled' ? 'destructive' : 'secondary'}>
                          {r.maps_to}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs space-y-0.5">
                        {r.samples.map((s) => (
                          <div key={s.smoobu_id} className="flex gap-2">
                            <span className="font-mono">{s.smoobu_id}</span>
                            <span className="text-muted-foreground">{s.guest_name}</span>
                            <span className="text-muted-foreground">{s.check_in}</span>
                            <Badge variant="outline" className="text-[10px] h-4">
                              {s.db_status}
                            </Badge>
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        {r.flagged && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
